import { lazy, Suspense, type ReactNode } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DetailsSection, EntityHeader, ErrorState, PageLayout } from '@eis/patterns';
import { useGroupGenerations, useGroupTarif } from '../api/group-tarifs';

/** Lazily loaded: the authoring editors are their own chunk (see apps/eis-console/vite.config). */
const GroupTarifAssets = lazy(async () => ({
  default: (await import('../../tarif-authoring/ui/GroupTarifAssets')).GroupTarifAssets,
}));
const GroupTarifAddGenerationPanel = lazy(async () => ({
  default: (await import('../../tarif-authoring/ui/AddGenerationPanel'))
    .GroupTarifAddGenerationPanel,
}));
const GenerationPreview = lazy(async () => ({
  default: (await import('../../tarif-authoring/ui/deckung/overview/GenerationPreview'))
    .GenerationPreview,
}));

/**
 * One group tarif and its generations.
 *
 * The generation preview is the tarif authoring one (ui-spec §10), run in group mode: a group tarif
 * REFERENCES a tarif and never the other way round (service summary §3.2), so the group surface
 * leans on the single-tarif one and not the reverse.
 *
 * The linked tarif is stated in the header and is not a link the operator can change — the 1:1
 * link is immutable at creation, and a surface that offered to edit it would be describing a
 * model this platform does not have.
 */
export function GroupTarifDetailPage({ slug }: { readonly slug: string }): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const groupTarif = useGroupTarif(slug);
  const generations = useGroupGenerations(slug);

  if (groupTarif.isError || generations.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={t('groupTarifs.error.detail')}
          details={(groupTarif.error ?? generations.error)?.message}
          onRetry={() => {
            void groupTarif.refetch();
            void generations.refetch();
          }}
        />
      </PageLayout>
    );
  }

  if (groupTarif.data === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={220} />
      </PageLayout>
    );
  }

  const entity = groupTarif.data.groupTarif;
  const rows = generations.data?.generations ?? [];

  return (
    <PageLayout
      header={
        <EntityHeader
          title={entity?.name ?? slug}
          subtitle={entity?.ern}
          // Berry has no breadcrumb band: the way back is the header's own link (prompt 24).
          backTo={{
            label: t('groupTarifs.title'),
            href: '/insurance/group-tarifs',
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: '/insurance/group-tarifs' });
            },
          }}
          meta={[
            { label: t('groupTarifs.detail.tarif'), value: entity?.tarifErn },
            { label: t('groupTarifs.detail.generationCount'), value: rows.length },
          ]}
        />
      }
    >
      <DetailsSection
        title={t('groupTarifs.detail.identity')}
        fields={[
          { label: t('groupTarifs.detail.name'), value: entity?.name },
          // Immutable at creation: one child definition, zero drift (service summary §3.2).
          { label: t('groupTarifs.detail.tarif'), value: entity?.tarifErn },
        ]}
        advancedFields={[
          { label: t('groupTarifs.detail.ern'), value: entity?.ern },
          { label: t('groupTarifs.detail.slug'), value: entity?.slug },
        ]}
      />

      <Suspense fallback={<Skeleton variant="rounded" height={240} />}>
        <GroupTarifAssets slug={slug} />
      </Suspense>

      {/*
        The tarif generation preview (ui-spec §10), one layer up: each group version's RESOLVED
        variants (the child's document under the group's override, allowed variants only).
      */}
      <Suspense fallback={<Skeleton variant="rounded" height={160} />}>
        <GenerationPreview
          owner="group"
          slug={slug}
          ownerLabel={entity?.name ?? slug}
          generations={rows}
          isPending={generations.isPending}
          emptyText={t('groupTarifs.generations.empty')}
          addGeneration={
            <GroupTarifAddGenerationPanel
              variant="footer"
              slug={slug}
              ownerVersion={groupTarif.data.version?.version}
              onGoToAssets={() => {
                document.querySelector('[data-eis-assets]')?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          }
        />
      </Suspense>
    </PageLayout>
  );
}
